import React, { useRef, useState } from "react";
import { HiX, HiCheck } from "react-icons/hi";

interface AddNegativeInfluencesLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (negativeInfluencesLog: {
    selectedInfluences: string[];
    notes: string;
    pointsAmount: number;
    logId?: string;
    logDate?: string;
  }) => Promise<void> | void;
  dateID: string;
  editMode?: boolean;
  initialData?: {
    selectedInfluences: string[];
    notes: string;
    pointsAmount?: number;
  };
  logId?: string;
}

const negativeInfluencesWithPoints = [
  { name: "Long-haul flight (10+ hrs)", points: 15 },
  { name: "Alcohol (5+ standard drinks)", points: 12 },
  { name: "Medium-haul flight (6-10 hrs)", points: 10 },
  { name: "Alcohol (3-4 standard drinks)", points: 8 },
  { name: "Alcohol (1-2 standard drinks)", points: 5 },
  { name: "Short-haul flight (<6 hrs)", points: 5 },
  { name: "Headache and minor illness symptoms", points: 5 },
  { name: "Other negative stress", points: 5 },
  { name: "Excessive caffeine (after midday)", points: 3 },
];

const AddNegativeInfluencesLogForm: React.FC <AddNegativeInfluencesLogFormProps
> = ({
  open,
  onClose,
  onSubmit,
  dateID,
  editMode = false,
  initialData,
  logId,
}) => {
  const [selectedInfluences, setSelectedInfluences] = useState<string[]>(
    initialData?.selectedInfluences || [],
  );
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  // Convert between a YYYYMMDD dateID and a YYYY-MM-DD <input type="date"> value
  const dateIDToInputValue = (id: string): string => {
    if (!id || id.length < 8) return "";
    return `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`;
  };
  const inputValueToDateID = (val: string): string => val.replace(/-/g, "");

  const [dateInput, setDateInput] = useState<string>(
    dateIDToInputValue(dateID),
  );
  const dateInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () => {
    const el = dateInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch {
      el.focus();
    }
  };

  // Calculate total points based on selected negative influences
  // (stored as a positive magnitude; sign is applied later when netting
  // against recovery points on the dashboard)
  const calculateTotalPoints = (influences: string[]): number => {
    return influences.reduce((total, influence) => {
      const item = negativeInfluencesWithPoints.find(
        (ni) => ni.name === influence,
      );
      return total + (item?.points || 0);
    }, 0);
  };

  const totalPoints = calculateTotalPoints(selectedInfluences);

  const formatDate = (dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    if (selectedInfluences.length === 0) return;

    setIsSubmitting(true);
    try {
      const logData: {
        selectedInfluences: string[];
        notes: string;
        pointsAmount: number;
        logId?: string;
        logDate?: string;
      } = {
        selectedInfluences,
        notes: notes.trim(),
        pointsAmount: totalPoints,
        logDate: inputValueToDateID(dateInput) || dateID,
      };

      if (editMode && logId) {
        logData.logId = logId;
      }

      await onSubmit(logData);
      handleClose();
    } catch (error) {
      console.error("Error adding negative influences log:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (editMode && initialData) {
      setSelectedInfluences(initialData.selectedInfluences);
      setNotes(initialData.notes);
    } else {
      setSelectedInfluences([]);
      setNotes("");
    }
    onClose();
  };

  // Update form values when initial data changes
  React.useEffect(() => {
    if (editMode && initialData) {
      setSelectedInfluences(initialData.selectedInfluences);
      setNotes(initialData.notes);
    } else if (!editMode) {
      setSelectedInfluences([]);
      setNotes("");
    }
    setDateInput(dateIDToInputValue(dateID));
  }, [editMode, initialData, open, dateID]);

  const toggleInfluence = (name: string) => {
    setSelectedInfluences((prev) =>
      prev.includes(name)
        ? prev.filter((m) => m !== name)
        : [...prev, name],
    );
  };

  if (!open) return null;

  const displayDateID = inputValueToDateID(dateInput) || dateID;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm overflow-y-auto"
      style={{ transform: "translateZ(0)" }}
    >
      <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
        <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start p-6 flex-shrink-0">
            <div>
              <div className="flex items-center gap-1">
                <h2 className="text-lg font-grotesk">
                  {editMode
                    ? "Edit Negative Influences"
                    : "Add Negative Influences"}
                </h2>
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                  type="button"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-base-content/50 hover:text-base-content/80 text-xs font-bold">
                    ?
                  </span>
                </button>
              </div>
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={openDatePicker}
                  className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors"
                >
                  <span className="font-medium underline decoration-dotted underline-offset-4">
                    {formatDate(displayDateID)}
                  </span>
                </button>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
            </div>
            <button
              onClick={handleClose}
              className="bg-base-200 p-2 rounded-full"
            >
              <HiX className="w-5 h-5 text-base-content/80" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div
            className="flex-1 overflow-y-auto p-4 md:p-6"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              transform: "translateZ(0)",
              maxHeight: "calc(100vh - 200px)",
              minHeight: "0",
            }}
          >
            {/* Negative Influences List */}
            <div className="form-control">
              <div className="gap-y-4 px-4">
                {negativeInfluencesWithPoints
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => toggleInfluence(item.name)}
                      className={`flex items-center w-full justify-between p-3 mb-1 rounded-lg border-2 transition-colors ${
                        selectedInfluences.includes(item.name)
                          ? "border-red-500 bg-red-500 bg-opacity-10 text-red-600"
                          : "border-base-200 hover:border-base-300 text-base-content/80"
                      }`}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="text-sm font-medium">
                          {item.name}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-base-200 text-base-content/80 px-2 py-1 rounded-full">
                            -<strong>{item.points}</strong> pts
                          </span>
                          {selectedInfluences.includes(item.name) && (
                            <HiCheck className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>

            {/* Total Points Display */}
            {selectedInfluences.length > 0 && (
              <div className="mt-4 p-3 bg-base-200 border-base-300 border-2 rounded-lg mx-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium font-grotesk text-base-content">
                      Total Negative Points
                    </span>
                  </div>
                  <span className="text-lg font-bold text-base-content">
                    -<strong>{totalPoints}</strong> pts
                  </span>
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="form-control pb-4 pt-6 mx-4">
              <textarea
                className="border-base-200 !text-[17px] border-2 p-4 rounded-md bg-base-200 textarea h-40 resize-none focus:outline-none focus:ring-0"
                style={{ lineHeight: "1.1" }}
                placeholder="Add your notes here... (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-end gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
            <button
              onClick={handleSubmit}
              disabled={selectedInfluences.length === 0 || isSubmitting}
              aria-label={editMode ? "Update negative influences" : "Log negative influences"}
              title={editMode ? "Update negative influences" : "Log negative influences"}
              className="pointer-events-auto w-14 h-14 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <HiCheck className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Information Dialog */}
      {showInfoDialog && (
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowInfoDialog(false)}
        >
          <div
            className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold font-grotesk text-base-content">
                  <span className="font-light">Tracking</span> Negative
                  Influences
                </h3>
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                >
                  <HiX className="w-5 h-5 text-base-content/60" />
                </button>
              </div>

              <div className="space-y-4 text-sm text-base-content/80">
                <p className="font-grotesk text-lg">
                  Negative influences are factors outside of training that
                  can reduce your readiness, such as travel, alcohol, illness,
                  stress, or excessive caffeine. Logging them provides context
                  alongside your recovery, wellness, and training load.
                </p>
                <p className="font-sans">
                  Negative influence points are subtracted from any recovery
                  points you've logged for the day. If your negative
                  influences outweigh your recovery, the excess is deducted
                  from your Peak Score itself, shown as a faded segment on
                  your Peak Score circle.
                </p>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="w-full bg-neutral text-white py-3 px-6 font-grotesk font-bold rounded-md hover:bg-neutral transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddNegativeInfluencesLogForm;